import { Screens, type AuthResponse } from '../types/auth'

/**
 * Opt-in: hosts (e.g. clientportal) defer {@link saveAuthAfterLogin} until the in-place
 * Set Password step completes. Default consumers omit this flag.
 */
export interface AuthDeferInPlacePasswordOptions {
  deferSessionUntilInPlacePasswordSet?: boolean
}

/**
 * API signals in-place set-password flow (token issued but password not verified yet).
 * Uses explicit `false` — `undefined` must not trigger deferral.
 */
export function needsInPlacePasswordSetup(data: AuthResponse): boolean {
  return Boolean(data?.token && data.passwordResetToken && data.isPasswordVerified === false)
}

export function buildPasswordSetContext(
  data: AuthResponse,
  emailFallback: string
): { email: string; contactId: string; passwordResetToken: string } {
  return {
    email: data.email ?? emailFallback,
    contactId: data.contactId,
    passwordResetToken: data.passwordResetToken ?? '',
  }
}

export interface DeferredInPlacePasswordLoginResult {
  success: true
  data: AuthResponse
  requiresPasswordSet: true
  passwordSetContext: ReturnType<typeof buildPasswordSetContext>
}

/**
 * When deferral is enabled and the API shape matches {@link needsInPlacePasswordSetup},
 * callers should skip session persistence and show in-place Set Password.
 */
export function tryDeferredInPlacePasswordLogin(
  data: AuthResponse,
  options: AuthDeferInPlacePasswordOptions | undefined,
  emailFallback: string
): DeferredInPlacePasswordLoginResult | null {
  if (!options?.deferSessionUntilInPlacePasswordSet || !needsInPlacePasswordSetup(data)) {
    return null
  }
  return {
    success: true,
    data,
    requiresPasswordSet: true,
    passwordSetContext: buildPasswordSetContext(data, emailFallback),
  }
}

/** Payload for `changeScreen` → Set Password (AuthHandler / useAuthPage). */
export function buildSetPasswordScreenChange(email: string, data: AuthResponse) {
  return {
    email: data.email ?? email,
    passwordResetToken: data.passwordResetToken,
    contactId: data.contactId,
    page: Screens.SET_PASSWORD,
  }
}

/**
 * Non-deferred hosts: navigate to Set Password when the response still carries an
 * unverified password + reset token (looser than {@link needsInPlacePasswordSetup},
 * which requires `isPasswordVerified === false`).
 */
export function shouldShowSetPasswordAfterLoginResponse(data: AuthResponse): boolean {
  return Boolean(data.token && !data.isPasswordVerified && data.passwordResetToken)
}

export function isDeferredInPlacePasswordLoginResult(deferEnabled: boolean, result: unknown): result is DeferredInPlacePasswordLoginResult {
  if (!deferEnabled || !result || typeof result !== 'object') return false
  const r = result as Record<string, unknown>
  return r.requiresPasswordSet === true && typeof r.data === 'object' && r.data !== null
}
